import React, { useState, useEffect } from 'react';
import Logo from "../../assets/logo.jpg";
import { getPrimaryClientLogo } from '../../utils/clientLogoService';

const PrintDashboard = React.forwardRef(({
    filteredData,
    allProgramCodes,
    selectedTab,
    participantsEnrolled,
    overallAverage,
    performanceConfig,
    currentClientInfo
}, ref) => {
    
    const [clientLogo, setClientLogo] = useState(null);
    
    // Function to fetch client logo using the new client logo service
    const fetchClientDetails = async (clientBatch) => {
        if (!clientBatch) return;

        try {
            const logoUrl = await getPrimaryClientLogo(clientBatch);
            setClientLogo(logoUrl);
        } catch (error) {
            console.error("Error fetching client logo:", error);
            setClientLogo(null);
        }
    };

    // Fetch client logo when component mounts or active program code changes
    useEffect(() => {
        const activeProgramCode = allProgramCodes.length > 0 ? allProgramCodes[selectedTab] : null;
        if (activeProgramCode) {
            fetchClientDetails(activeProgramCode);
        }
    }, [allProgramCodes, selectedTab]);
    
    // Helper function to get max score from assessment JSON or default to 60
    const getMaxScore = (assessment) => {
        if (assessment?.json) {
            try {
                const jsonData = typeof assessment.json === 'string' ? JSON.parse(assessment.json) : assessment.json;
                return jsonData.max_score || 60;
            } catch (error) {
                console.log('Error parsing JSON for max_score:', error);
                return 60;
            }
        }
        return assessment?.max_score || 60;
    };

    const getPerformanceDistribution = (responses) => {
        const distribution = {};  
        responses.forEach(user => {
            const level = user.json.finalInterpretation.name;
            distribution[level] = (distribution[level] || 0) + 1;
        });

        // Colors ordered by percentage (highest to lowest)
        const percentageBasedColors = [
            '#4caf50', // Green for highest percentage
            '#2196f3', // Blue for second highest
            '#ff9800', // Orange for third highest
            '#f44336', // Red for fourth highest
            '#9c27b0'  // Purple for lowest percentage
        ];

        // Sort entries by count (highest to lowest percentage)
        const sortedEntries = Object.entries(distribution).sort(([, countA], [, countB]) => countB - countA);

        return sortedEntries.map(([name, count], index) => {
            // Assign colors based on percentage ranking (highest gets first color)
            const color = percentageBasedColors[index] || percentageBasedColors[percentageBasedColors.length - 1];
            
            return {
                name: name.replace(/[🌟💎⚙️🛠🔴❓?�]/g, '').replace(/\?+/g, '').replace(/�+/g, '').trim(),
                fullName: name,
                count,
                color: color
            };
        });
    };

    // Chart to SVG conversion for bar charts - Full width for print
    const chartToSVG = (chartData, maxScore = 60, isLarge = false) => {
        // For print, use full container width
        const containerWidth = 700; // Full width available in print
        const availableChartWidth = containerWidth - 80; // Subtract margins
        const barSpacing = Math.max(availableChartWidth / chartData.length, 25); // Dynamic spacing
        const barWidth = Math.max(barSpacing * 0.7, 12); // Bar width based on spacing
        const chartHeight = 140;

        return `
            <svg width="${containerWidth}" height="${chartHeight + 80}" style="background: white;">
                <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" stroke-width="1"/>
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
                ${chartData.map((item, index) => {
                    const barHeight = Math.max((item.score / maxScore) * chartHeight, 3);
                    const x = index * barSpacing + 40 + (barSpacing - barWidth) / 2;
                    const y = chartHeight - barHeight + 20;
                    return `
                        <rect x="${x}" y="${y}" width="${barWidth}" height="${barHeight}" fill="#0a0068" stroke="#fff" stroke-width="1" rx="2"/>
                        <text x="${x + barWidth / 2}" y="${chartHeight + 40}" text-anchor="middle" font-size="9" fill="#666" transform="rotate(-45 ${x + barWidth / 2} ${chartHeight + 40})">${item.name.length > 10 ? item.name.substring(0, 10) + '...' : item.name}</text>
                        <text x="${x + barWidth / 2}" y="${y - 5}" text-anchor="middle" font-size="10" fill="#333" font-weight="bold">${item.score}</text>
                    `;
                }).join('')}
                <text x="20" y="25" font-size="11" fill="#666">${maxScore}</text>
                <text x="20" y="${chartHeight + 15}" font-size="11" fill="#666">0</text>
                <line x1="30" y1="20" x2="30" y2="${chartHeight + 20}" stroke="#666" stroke-width="1"/>
                <line x1="30" y1="${chartHeight + 20}" x2="${containerWidth - 20}" y2="${chartHeight + 20}" stroke="#666" stroke-width="1"/>
            </svg>
        `;
    };

    // Donut chart to SVG conversion with performance-based colors
    const pieChartToSVG = (data) => {
        const total = data.reduce((sum, item) => sum + item.count, 0);
        const outerRadius = 65;
        const innerRadius = 35;
        const centerX = 90;
        const centerY = 80;

        let currentAngle = 0;
        let paths = '';

        if (data.length === 1) {
            // For single item, create a donut (annulus)
            paths = `
                <circle cx="${centerX}" cy="${centerY}" r="${outerRadius}" fill="${data[0].color}" stroke="white" stroke-width="2"/>
                <circle cx="${centerX}" cy="${centerY}" r="${innerRadius}" fill="white"/>
            `;
        } else {
            paths = data.map((item, index) => {
                const percentage = item.count / total;
                const angle = percentage * 2 * Math.PI;

                // Outer arc points
                const x1Outer = centerX + outerRadius * Math.cos(currentAngle);
                const y1Outer = centerY + outerRadius * Math.sin(currentAngle);
                const x2Outer = centerX + outerRadius * Math.cos(currentAngle + angle);
                const y2Outer = centerY + outerRadius * Math.sin(currentAngle + angle);

                // Inner arc points
                const x1Inner = centerX + innerRadius * Math.cos(currentAngle);
                const y1Inner = centerY + innerRadius * Math.sin(currentAngle);
                const x2Inner = centerX + innerRadius * Math.cos(currentAngle + angle);
                const y2Inner = centerY + innerRadius * Math.sin(currentAngle + angle);

                const largeArcFlag = angle > Math.PI ? 1 : 0;

                // Create donut segment path
                const pathData = `
                    M ${x1Outer} ${y1Outer}
                    A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2Outer} ${y2Outer}
                    L ${x2Inner} ${y2Inner}
                    A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x1Inner} ${y1Inner}
                    Z
                `;

                currentAngle += angle;
                return `<path d="${pathData}" fill="${item.color}" stroke="white" stroke-width="2"/>`;
            }).join('');
        }

        // Legend positioned at top right
        const legend = data.map((item, index) => {
            return `
                <rect x="190" y="${10 + index * 14}" width="8" height="8" fill="${item.color}"/>
                <text x="202" y="${17 + index * 14}" font-size="8" fill="#333">
                    ${item.name}: ${item.count} (${Math.round((item.count / total) * 100)}%)
                </text>
            `;
        }).join('');

        return `
            <svg width="350" height="170" style="background: white;">
                ${paths}
                ${legend}
            </svg>
        `;
    };

    const getAvgAssessmentScore = (assessment) => {
        return assessment.assessmentResults?.length > 0 ?
            assessment.assessmentResults.reduce((sum, user) => sum + user.json.finalScore, 0) / assessment.assessmentResults.length : 0
    }

    // Get the active program code name
    const activeProgramCode = allProgramCodes.length > 0 ? allProgramCodes[selectedTab] : 'SRI';

    // Helper function to get assessment date range
    const getAssessmentDateRange = () => {
        if (filteredData.length === 0) return 'No assessment data available';
        
        const firstAssessment = filteredData[0];
        
        // Check for start_date and end_date fields in the assessment object
        if (firstAssessment.start_date && firstAssessment.end_date) {
            const startDate = new Date(firstAssessment.start_date);
            const endDate = new Date(firstAssessment.end_date);
            
            const formatDate = (date) => {
                return date.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                });
            };
            
            if (startDate.getTime() === endDate.getTime()) {
                return `Assessment Date: ${formatDate(startDate)}`;
            } else {
                return `Assessment Period: ${formatDate(startDate)} - ${formatDate(endDate)}`;
            }
        }
        
        // Fallback: check for start_date only
        if (firstAssessment.start_date) {
            const startDate = new Date(firstAssessment.start_date);
            const formatDate = (date) => {
                return date.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                });
            };
            return `Assessment Date: ${formatDate(startDate)}`;
        }
        
        // Final fallback: check assessment results for dates
        if (firstAssessment.assessmentResults && firstAssessment.assessmentResults.length > 0) {
            const dates = firstAssessment.assessmentResults.map(result => {
                if (result.json && result.json.assessment_date) {
                    return new Date(result.json.assessment_date);
                }
                return null;
            }).filter(date => date !== null);
            
            if (dates.length > 0) {
                const startDate = new Date(Math.min(...dates));
                const endDate = new Date(Math.max(...dates));
                
                const formatDate = (date) => {
                    return date.toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                    });
                };
                
                if (startDate.getTime() === endDate.getTime()) {
                    return `Assessment Date: ${formatDate(startDate)}`;
                } else {
                    return `Assessment Period: ${formatDate(startDate)} - ${formatDate(endDate)}`;
                }
            }
        }
        
        return 'Assessment dates not available';
    };

    // Header component that can be repeated
    const PrintHeader = () => (
        <div className="print-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', position: 'relative' }}>
                {/* Left side - Talent Sculptors Logo */}
                <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                    <img src={Logo} alt="Logo" style={{ height: '30px' }} />
                </div>
                
                {/* Center - Title */}
                <div style={{ 
                    position: 'absolute', 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    textAlign: 'center',
                    zIndex: 1
                }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0a0068' }}>
                        Assessment Report - {activeProgramCode}
                    </div>
                    <div style={{ fontSize: '10px', color: '#666' }}>
                        {getAssessmentDateRange()}
                    </div>
                </div>
                
                {/* Right side - Client Logo */}
                <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                    {clientLogo && (
                        <img 
                            src={clientLogo} 
                            alt="Client Logo" 
                            style={{ 
                                height: '35px', 
                                maxWidth: '120px', 
                                objectFit: 'contain'
                            }} 
                        />
                    )}
                </div>
            </div>
            <hr style={{ 
                border: 'none', 
                borderTop: '2px solid #0a0068', 
                margin: '10px 0', 
                width: '100%' 
            }} />
            <br/>
        </div>
    );

    // Footer component that can be repeated
    const PrintFooter = () => (
        <div className="print-footer">
            <div style={{ fontSize: '10px', color: '#666' }}>
                <span>Automated Report Generated by Talent Sculptors - Career Excellence | © {new Date().getFullYear()} All rights reserved.</span>
            </div>
        </div>
    );

    return (
        <div ref={ref} className="print-wrapper" style={{ fontFamily: 'Arial, sans-serif', backgroundColor: 'white', color: 'black', lineHeight: '1.4' }}>
            {/* Main Page Header */}
            <PrintHeader />

            {/* Main Content */}
            <div className="print-content">
                {/* CES Leaders' Launchpad Introduction - Only for specific client, takes full first page */}
                {activeProgramCode === 'CES-Leaders-Launchpad' && (
                    <section className="no-break" style={{ marginBottom: '30px', minHeight: '60vh' }}>
                        <h2 style={{ color: '#0a0068', marginBottom: '20px', fontSize: '16px', borderBottom: '2px solid #f0f0f0', paddingBottom: '5px' }}>
                            Introduction
                        </h2>
                        <div style={{ fontSize: '12px', lineHeight: '1.6', color: '#333' }}>
                            <p style={{ marginBottom: '15px' }}>
                                The CES Leaders' Launchpad (3 hour) program is designed to support first-time managers in transitioning from strong individual contributors to effective people leaders. This program provides participants with the mindset, skills, and tools required to manage teams, drive collaboration, and embody CES values in daily leadership practice.
                            </p>
                            <p style={{ marginBottom: '20px' }}>
                                This assessment report captures the results of the pre-workshop evaluations conducted with participants. These assessments were designed and facilitated by Talent Sculptors to establish a baseline understanding of participants' leadership orientation and areas of development.
                            </p>
                            
                            <h3 style={{ color: '#cc4049', marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                                Purpose of the Assessments
                            </h3>
                            <p style={{ marginBottom: '10px' }}>
                                The assessments aimed to evaluate participants across three key dimensions critical for early-stage leaders:
                            </p>
                            <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                                <li style={{ marginBottom: '5px' }}><strong>Understanding the Role of a Team Lead</strong> – Clarity on responsibilities, people management, and leadership mindset.</li>
                                <li style={{ marginBottom: '5px' }}><strong>Managing Time and Priorities</strong> – Ability to balance responsibilities, plan effectively, and avoid burnout.</li>
                                <li style={{ marginBottom: '5px' }}><strong>People-Oriented Leadership Mindset</strong> – Empathy, collaboration, and a human-centered leadership approach.</li>
                            </ul>
                            <p style={{ marginBottom: '20px' }}>
                                These assessments provide valuable insights into participants' strengths and growth areas, setting the stage for a focused and impactful learning journey.
                            </p>
                            
                            <h3 style={{ color: '#cc4049', marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                                Desired Outcomes
                            </h3>
                            <p style={{ marginBottom: '10px' }}>
                                Through this program, participants are expected to:
                            </p>
                            <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
                                <li style={{ marginBottom: '5px' }}>Recognize and embrace their responsibilities as people leaders.</li>
                                <li style={{ marginBottom: '5px' }}>Dedicate 3–4 hours weekly to meaningful team engagement, building trust and morale.</li>
                                <li style={{ marginBottom: '5px' }}>Manage time, expectations, and priorities without feeling overwhelmed.</li>
                                <li style={{ marginBottom: '5px' }}>Deliver constructive and confident feedback to enhance team performance.</li>
                                <li style={{ marginBottom: '5px' }}>Embed and promote core CES values in all interactions:</li>
                            </ul>
                            <ul style={{ marginBottom: '20px', paddingLeft: '40px' }}>
                                <li style={{ marginBottom: '3px' }}>Apply Empathy</li>
                                <li style={{ marginBottom: '3px' }}>Listen & Learn</li>
                                <li style={{ marginBottom: '3px' }}>Customer Focus</li>
                                <li style={{ marginBottom: '3px' }}>Respect People & Process</li>
                                <li style={{ marginBottom: '3px' }}>Think Objectively</li>
                                <li style={{ marginBottom: '3px' }}>Seek Feedback & Transform</li>
                            </ul>
                            <p style={{ marginBottom: '0', fontStyle: 'italic' }}>
                                This report provides a consolidated view of participant performance, helping leaders and stakeholders align on development priorities before the workshop begins.
                            </p>
                        </div>
                        {/* Force page break after introduction */}
                        <div style={{ pageBreakAfter: 'always', breakAfter: 'page' }}></div>
                    </section>
                )}

                {/* Executive Summary Section - starts on new page for CES-Leaders-Launchpad */}
                <section className="no-break" style={{ marginBottom: '25px' }}>
                    <h2 style={{ color: '#0a0068', marginBottom: '15px', fontSize: '16px', borderBottom: '2px solid #f0f0f0', paddingBottom: '5px' }}>
                        Executive Summary
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '15px' }}>
                        <div style={{ padding: '15px', background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)', borderRadius: '8px', textAlign: 'center', color: 'white' }}>
                            <p style={{ color: 'rgba(255,255,255,0.9)', margin: '0 0 5px 0', fontSize: '18px', fontWeight: '600' }}>{filteredData.length}</p>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '11px', fontWeight: '600' }}>Total Assessments</p>
                        </div>
                        <div style={{ padding: '15px', background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)', borderRadius: '8px', textAlign: 'center', color: 'white' }}>
                            <p style={{ color: 'rgba(255,255,255,0.9)', margin: '0 0 5px 0', fontSize: '18px', fontWeight: '600' }}>{participantsEnrolled}</p>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '11px', fontWeight: '600' }}>Participants Enrolled</p>
                        </div>
                        <div style={{ padding: '15px', background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)', borderRadius: '8px', textAlign: 'center', color: 'white' }}>
                            <p style={{ color: 'rgba(255,255,255,0.9)', margin: '0 0 5px 0', fontSize: '18px', fontWeight: '600' }}>{overallAverage}</p>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '11px', fontWeight: '600' }}>Average Score</p>
                        </div>
                        <div style={{ padding: '15px', background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)', borderRadius: '8px', textAlign: 'center', color: 'white' }}>
                            <p style={{ color: 'rgba(255,255,255,0.9)', margin: '0 0 5px 0', fontSize: '18px', fontWeight: '600' }}>{activeProgramCode}</p>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '11px', fontWeight: '600' }}>Active Client</p>
                        </div>
                    </div>
                </section>

                {/* Program Description Section */}
                {currentClientInfo && currentClientInfo.description && (
                    <section className="no-break" style={{ marginBottom: '25px' }}>
                        <h2 style={{ color: '#0a0068', marginBottom: '15px', fontSize: '16px', borderBottom: '2px solid #f0f0f0', paddingBottom: '5px' }}>
                            About {currentClientInfo.programCode || 'This Program'}
                        </h2>
                        <div style={{ 
                            padding: '15px', 
                            backgroundColor: '#f9f9f9', 
                            borderRadius: '8px', 
                            border: '1px solid #e9ecef',
                            fontSize: '12px', 
                            lineHeight: '1.6', 
                            color: '#333'
                        }}>
                            <div dangerouslySetInnerHTML={{ 
                                __html: currentClientInfo.description 
                            }} />
                        </div>
                    </section>
                )}

                {/* Assessment Analysis Section - Combined charts and performance flow together */}
                <section className="major-section" >
                    <h2 style={{ color: '#0a0068', marginBottom: '20px', fontSize: '16px', borderBottom: '2px solid #f0f0f0', paddingBottom: '5px' }}>
                        Assessment Analysis
                    </h2>
                    
                    {filteredData.map((assessment, index) => {
                        const hasMany = assessment.assessmentResults.length >= 15;
                        const distribution = getPerformanceDistribution(assessment.assessmentResults);
                        
                        return (
                            <div key={assessment.id} className="assessment-card no-break" style={{
                                border: '1px solid #e9ecef',
                                borderRadius: '6px',
                                padding: '15px',
                                backgroundColor: '#fafafa',
                                marginBottom: '20px',
                                marginTop: '40px'
                            }}>
                                <h3 style={{ color: '#0a0068', margin: '0 0 12px 0', fontSize: '14px', fontWeight: 'bold' }}>
                                    {assessment.assignment_name}
                                </h3>
                                
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
                                    <span style={{ padding: '3px 8px', backgroundColor: '#f0f0f0', borderRadius: '8px', fontSize: '10px', fontWeight: '600' }}>
                                        👥 {assessment.assessmentResults.length} Participants
                                    </span>
                                    <span style={{ padding: '3px 8px', backgroundColor: '#e8f5e8', color: '#2e7d32', borderRadius: '8px', fontSize: '10px', fontWeight: '600' }}>
                                        📈 Avg: {Math.round(getAvgAssessmentScore(assessment))}/{getMaxScore(assessment)}
                                    </span>
                                </div>
                                
                                {/* Full Width Score Chart */}
                                <div style={{ 
                                    backgroundColor: 'white', 
                                    padding: '15px', 
                                    borderRadius: '4px', 
                                    border: '1px solid #e9ecef',
                                    marginBottom: '15px',
                                    width: '100%'
                                }}>
                                    <h4 style={{ color: '#0a0068', margin: '0 0 12px 0', fontSize: '12px', fontWeight: 'bold' }}>
                                        Individual Scores
                                    </h4>
                                    <div dangerouslySetInnerHTML={{
                                        __html: chartToSVG(
                                            assessment.assessmentResults.map(user => ({
                                                name: `${user.json.user_details.fname} ${user.json.user_details.lname}`.trim() || 'User',
                                                score: user.json.finalScore
                                            })),
                                            getMaxScore(assessment),
                                            hasMany
                                        )
                                    }} />
                                </div>
                                
                                {/* Performance Distribution Chart */}
                                <div style={{ 
                                    backgroundColor: 'white', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    border: '1px solid #e9ecef',
                                    textAlign: 'center'
                                }}>
                                    <h4 style={{ color: '#0a0068', margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold' }}>
                                        Performance Distribution
                                    </h4>
                                    <div dangerouslySetInnerHTML={{ __html: pieChartToSVG(distribution) }} />
                                </div>
                            </div>
                        );
                    })}
                </section>

                {/* Detailed User Performance Section - flows naturally after previous content */}
                {filteredData.length > 0 && (
                    <section style={{ marginBottom: '25px', marginTop: '30px' }}>
                        <h2 style={{ color: '#0a0068', marginBottom: '15px', fontSize: '16px', borderBottom: '2px solid #f0f0f0', paddingBottom: '5px' }}>
                            Detailed User Performance
                        </h2>
                        {filteredData.map((assessment) => (
                            <div key={assessment.id} style={{ marginBottom: '25px' }}>
                                <h3 style={{ color: '#cc4049', marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                                    {assessment.assignment_name}
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                    {assessment.assessmentResults.map((user) => (
                                        <div key={user.user_id} className="no-break" style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '6px',
                                            padding: '10px',
                                            backgroundColor: '#fafafa'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                                <div style={{
                                                    width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#0a0068',
                                                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontWeight: 'bold', fontSize: '12px', marginRight: '8px'
                                                }}>
                                                    {user.json.user_details.fname?.charAt(0) || 'U'}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 'bold', fontSize: '11px', color: '#333', marginBottom: '1px' }}>
                                                        {user.json.user_details.fname} {user.json.user_details.lname}
                                                    </div>
                                                    <div style={{ fontSize: '9px', color: '#666' }}>
                                                        {user.json.user_details.email}
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ marginBottom: '8px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                    <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#0a0068' }}>
                                                        {user.json.finalScore}/{getMaxScore(assessment)}
                                                    </span>
                                                    <span style={{ fontSize: '10px', color: '#666' }}>
                                                        {Math.round((user.json.finalScore / getMaxScore(assessment)) * 100)}%
                                                    </span>
                                                </div>
                                                <div style={{ width: '100%', height: '4px', backgroundColor: '#e9ecef', borderRadius: '2px', overflow: 'hidden' }}>
                                                    <div style={{
                                                        width: `${(user.json.finalScore / getMaxScore(assessment)) * 100}%`,
                                                        height: '100%', backgroundColor: '#0a0068', borderRadius: '2px'
                                                    }}></div>
                                                </div>
                                            </div>
                                            <div style={{
                                                padding: '4px 8px', borderRadius: '12px',
                                                backgroundColor: performanceConfig[user.json.finalInterpretation.name]?.bgColor || '#f5f5f5',
                                                color: performanceConfig[user.json.finalInterpretation.name]?.textColor || '#666',
                                                fontSize: '9px', fontWeight: 'bold', textAlign: 'center', marginBottom: '8px',
                                                border: `1px solid ${performanceConfig[user.json.finalInterpretation.name]?.color || '#ccc'}`
                                            }}>
                                                {user.json.finalInterpretation.name}
                                            </div>
                                            <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '6px', fontSize: '9px', color: '#666' }}>
                                                <div style={{ marginBottom: '3px' }}>
                                                    <strong>Client:</strong> {user.json.user_details.user_batch}
                                                </div>
                                                <div>
                                                    <strong>Experience:</strong> {user.json.user_details.experience_level || 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                )}
            </div>
            
            {/* Sticky Footer - will repeat on all pages */}
            <PrintFooter />
        </div>
    );
});

export default PrintDashboard;